import * as THREE from 'three'
import { classifyGSplatClipNode } from '@/plugins/gsplat/clipping/classifyGSplatClipNode'
import {
  areGSplatClipStatesEqual,
  type GSplatClipState,
} from '@/plugins/gsplat/clipping/GSplatClipState'
import type { IGSplatResource } from '@/plugins/gsplat/types/ResourceTypes'
import { getLoggerManager } from '@/shared/logging'
import {
  type CompiledGSplatWorkBufferModifier,
  GSPLAT_WORKBUFFER_UPDATE_AUTO,
  GSPLAT_WORKBUFFER_UPDATE_ONCE,
  type GSplatClipLodDiagnostics,
  type GSplatLodRefinementMode,
  type GSplatWorkBufferUpdateMode,
} from '@/shared/types/gsplat'
import { NUM_BUCKETS } from './constants'
import type { GSplatOctree, GSplatOctreeNode, GSplatOctreeNodeLod } from './GSplatOctree'
import type { GSplatParams } from './GSplatParams'
import { GSplatPlacement, type Interval } from './GSplatPlacement'

const log = getLoggerManager().getLogger('gsplat:lod')

/**
 * 节点 LOD 状态信息
 *
 * 存储每个八叉树节点的 LOD 级别和重要性
 */
class NodeInfo {
  /**
   * 当前渲染的 LOD 索引
   * -1 表示节点不可见
   */
  currentLod: number = -1

  /**
   * 基于距离计算的最优 LOD 索引（应用预算之前）
   */
  optimalLod: number = -1

  /**
   * 节点重要性（0-1 范围，越高越重要）
   * 用于预算执行时的优先级排序
   */
  importance: number = 0

  /** World-space distance used by global budget bucketing. */
  worldDistance: number = 0

  /** Owning instance, used by the global budget balancer. */
  inst: GSplatOctreeInstance | null = null

  /** Cached node LOD list for global budget adjustment loops. */
  lods: GSplatOctreeNodeLod[] | null = null

  /** Distance bucket [0, NUM_BUCKETS - 1] for global budget balancing. */
  budgetBucket: number = 0

  /**
   * 重置所有 LOD 值为 -1（不可见/未初始化）
   */
  reset(): void {
    this.currentLod = -1
    this.optimalLod = -1
    this.importance = 0
    this.worldDistance = 0
    this.lods = null
    this.budgetBucket = 0
  }
}

// 临时变量，避免每帧分配
const _invWorldMat = new THREE.Matrix4()
const _localCameraPos = new THREE.Vector3()
const _localCameraFwd = new THREE.Vector3()
const _dirToNode = new THREE.Vector3()
const _worldCameraPos = new THREE.Vector3()
const _worldCameraFwd = new THREE.Vector3()
const _closestPoint = new THREE.Vector3()

const _tempCompletedUrls: number[] = []
const REF_TAN_HALF_FOV = Math.tan(THREE.MathUtils.degToRad(22.5))

function createClipLodDiagnostics(): GSplatClipLodDiagnostics {
  return {
    classifiedNodes: 0,
    polygonCulledNodes: 0,
    boxCulledNodes: 0,
    mixedCulledNodes: 0,
    intersectingNodes: 0,
    excludedSplats: 0,
  }
}

/**
 * 待处理的引用减少记录
 */
interface PendingDecrement {
  oldFileIndex: number // 旧文件索引
  newFileIndex: number // 新文件索引
}

/**
 * GSplat 八叉树实例
 *
 * 核心功能：
 * 1. LOD 评估和选择（基于距离、重要性、预算）
 * 2. 文件引用计数管理
 * 3. 预加载和流式加载控制
 * 4. Underfill 策略实现
 *
 * 对应 PlayCanvas 的 GSplatOctreeInstance 类
 */
export class GSplatOctreeInstance {
  /** 关联的八叉树 */
  octree: GSplatOctree

  /** 根 Placement（用于获取 node、lodDistances、splatBudget） */
  placement: GSplatPlacement

  /** 激活的 Placement 集合（实际渲染的） */
  activePlacements: Set<GSplatPlacement> = new Set()

  /** 标记 Placement 已修改，需要更新 */
  dirtyModifiedPlacements: boolean = false

  /** 节点 LOD 状态数组（每个八叉树节点一个 NodeInfo） */
  nodeInfos: NodeInfo[]

  /** 文件索引 -> Placement 映射表 */
  filePlacements: (GSplatPlacement | null)[]

  /** 正在加载的文件索引集合 */
  pending: Set<number> = new Set()

  /**
   * 待处理的引用减少映射表
   * Key: 节点索引
   * Value: { oldFileIndex, newFileIndex }
   *
   * 确保即使节点再次切换 LOD，旧文件也能正确 decRef
   */
  pendingDecrements: Map<number, PendingDecrement> = new Map()

  /** 上次 LOD 更新时的位置（用于检测移动） */
  previousPosition: THREE.Vector3 = new THREE.Vector3()

  /** 标记需要重新评估 LOD */
  needsLodUpdate: boolean = false

  /**
   * 正在预加载的文件索引集合
   * 当完成时触发 LOD 重新评估以允许晋升
   */
  prefetchPending: Set<number> = new Set()

  /**
   * 不可见->可见的待处理添加映射表
   * Key: 节点索引
   * Value: 文件索引
   *
   * 确保节点在首次显示前只有一个待处理的 Placement
   */
  pendingVisibleAdds: Map<number, number> = new Map()

  /** 缓存的 Splat 预算值 */
  splatBudget: number = 0

  /** Clamped LOD range used by the global budget balancer. */
  rangeMin: number = 0
  rangeMax: number = 0

  /** 当前实例用于叶节点粗裁的最新编译快照。 */
  private clipState: GSplatClipState | null

  private _clipLodDiagnostics = createClipLodDiagnostics()

  /**
   * 当前帧已加载的节点数量（用于限流）
   */
  private loadedThisFrame: number = 0

  /**
   * 可复用的节点索引数组（用于预算执行时排序）
   * 懒加载分配，之后复用
   */
  private _nodeIndices: Uint32Array | null = null

  /**
   * 可复用的几何 LOD 距离阈值缓存。
   */
  private lodMinDistThresholds: Float32Array | null = null

  /** Environment Placement（可选） */
  environmentPlacement: GSplatPlacement | null = null

  /**
   * 实例可见性标志
   * 控制整个 LOD 实例的显示/隐藏（不影响资源加载和引用计数）
   */
  private _visible: boolean = true

  /**
   * WebGL 渲染器引用（用于上下文丢失处理）
   */
  private renderer: THREE.WebGLRenderer | null = null

  /**
   * 强制刷新标志（用于上下文恢复后重建资源）
   */
  private forceRefresh = false

  /**
   * 预算警告标志（避免重复警告）
   */
  private _warnedBudget = false

  /** 首个 pending resource 接入 activePlacements 的日志只输出一次。 */
  private firstActivePlacementLogged = false

  /** 当前实例是否持有 Octree environment 引用。 */
  private ownsEnvironmentRef = false

  /** 防止重复销毁造成共享资源引用重复递减。 */
  private _destroyed = false

  /**
   * 创建新的八叉树实例
   *
   * @param octree - 八叉树资源
   * @param placement - 根 Placement
   * @param renderer - WebGL 渲染器（可选，用于上下文丢失处理）
   */
  constructor(octree: GSplatOctree, placement: GSplatPlacement, renderer?: THREE.WebGLRenderer) {
    this.octree = octree
    this.placement = placement
    this.clipState = placement.clipState
    this.renderer = renderer || null

    // 初始化节点信息数组
    this.nodeInfos = new Array(octree.nodes.length)
    for (let i = 0; i < octree.nodes.length; i++) {
      const nodeInfo = new NodeInfo()
      nodeInfo.inst = this
      this.nodeInfos[i] = nodeInfo
    }

    // 初始化文件 Placement 数组
    const numFiles = octree.files.length
    this.filePlacements = new Array(numFiles).fill(null)

    // 处理 Environment（如果配置）
    if (octree.environmentUrl) {
      octree.incEnvironmentRefCount()
      this.ownsEnvironmentRef = true
      try {
        octree.ensureEnvironmentResource()
      } catch (error) {
        this.releaseEnvironmentRef()
        throw error
      }
    }

    // 设置上下文丢失处理
    if (this.renderer) {
      this.setupContextLossHandling(this.renderer)
    }

    // 确保首次加载时触发 LOD 评估
    // 即使 sorter.jobsInFlight >= 3，也能通过 anyInstanceNeedsLodUpdate 触发
    this.needsLodUpdate = true
  }

  /**
   * 设置 WebGL 上下文丢失/恢复事件处理
   * @param renderer - WebGL 渲染器
   */
  private setupContextLossHandling(renderer: THREE.WebGLRenderer): void {
    const canvas = renderer.domElement

    canvas.addEventListener('webglcontextlost', this.onContextLost)
    canvas.addEventListener('webglcontextrestored', this.onContextRestored)
  }

  /**
   * 上下文丢失事件处理
   *
   * @remarks
   * 参照 PlayCanvas 的 _onDeviceLost() 实现
   * 完整清理所有内部状态，确保上下文恢复后能正确重建
   */
  private onContextLost = (event: Event): void => {
    if (this._destroyed) return

    event.preventDefault()
    log.warn('GSplatOctreeInstance: WebGL context lost')

    // 1. 减少所有已加载文件的引用计数
    for (let i = 0; i < this.filePlacements.length; i++) {
      if (this.filePlacements[i]) {
        this.octree.decRefCount(i, 0)
      }
    }

    // context 已失效，本实例不再关注未完成请求；仅在共享 Octree 没有其他引用或关注时取消。
    this.cancelAllFileLoads()

    // 2. 清理所有内部状态
    this.filePlacements.fill(null)
    this.activePlacements.clear()
    this.pendingDecrements.clear()
    this.pendingVisibleAdds.clear()

    // 3. 重置所有节点为不可见
    for (const nodeInfo of this.nodeInfos) {
      nodeInfo.reset()
    }

    // 4. 同一 Octree 的 Instance 共用 renderer/context；context 丢失会让共享 environment
    // 对所有实例同时失效。保留所有权计数，但幂等取消旧请求，恢复后重新 ensure。
    if (this.environmentPlacement) {
      this.activePlacements.delete(this.environmentPlacement)
      this.environmentPlacement = null
    }
    if (this.ownsEnvironmentRef && this.octree.environmentUrl) {
      this.octree.unloadEnvironmentResource()
    }

    // 5. 标记需要更新
    this.dirtyModifiedPlacements = true
    this.needsLodUpdate = true
  }

  /**
   * 上下文恢复事件处理
   */
  private onContextRestored = (): void => {
    if (this._destroyed) return

    // 触发资源重建
    this.forceRefresh = true
    this.needsLodUpdate = true
  }

  /**
   * 清理上下文丢失处理监听器
   */
  private cleanupContextLossHandling(): void {
    if (this.renderer) {
      const canvas = this.renderer.domElement
      canvas.removeEventListener('webglcontextlost', this.onContextLost)
      canvas.removeEventListener('webglcontextrestored', this.onContextRestored)
    }
  }

  /**
   * 销毁实例并清理引用
   *
   * @param cooldownTicks - Optional cooldown override; use `0` for deterministic manager teardown.
   */
  destroy(cooldownTicks?: number): void {
    if (this._destroyed) return
    this._destroyed = true

    try {
      // 清理上下文丢失监听
      this.cleanupContextLossHandling()

      // 只在八叉树未销毁时才 decRef
      if (this.octree && !this.octree.destroyed) {
        // 减少所有已加载文件的引用计数
        const filesToDecRef = this.getFileDecrements()
        for (const fileIndex of filesToDecRef) {
          if (cooldownTicks === undefined) {
            this.octree.decRefCount(fileIndex)
          } else {
            this.octree.decRefCount(fileIndex, cooldownTicks)
          }
        }
      }
    } finally {
      try {
        // pending 与 prefetch 各自持有独立关注；实例销毁时全部成对释放，并由 Octree
        // 判断共享 URL 是否真的可以取消。
        this.cancelAllFileLoads()
      } finally {
        try {
          this.releaseEnvironmentRef()
        } finally {
          this.clearDestroyedState()
        }
      }
    }
  }

  private addFileLoadInterest(files: Set<number>, fileIndex: number): void {
    if (files.has(fileIndex)) {
      return
    }

    files.add(fileIndex)
    this.octree.incLoadInterest(fileIndex)
  }

  private completeFileLoadInterest(files: Set<number>, fileIndex: number): void {
    if (!files.delete(fileIndex)) {
      return
    }

    this.octree.decLoadInterest(fileIndex)
  }

  private cancelFileLoadInterest(files: Set<number>, fileIndex: number): void {
    if (!files.delete(fileIndex)) {
      return
    }

    this.octree.decLoadInterest(fileIndex)
    this.octree.cancelResourceLoadIfUnused(fileIndex)
  }

  private cancelAllFileLoads(): void {
    for (const fileIndex of this.pending) {
      this.cancelFileLoadInterest(this.pending, fileIndex)
    }
    for (const fileIndex of this.prefetchPending) {
      this.cancelFileLoadInterest(this.prefetchPending, fileIndex)
    }
  }

  private releaseEnvironmentRef(): void {
    if (!this.ownsEnvironmentRef) {
      return
    }

    this.ownsEnvironmentRef = false
    this.octree.decEnvironmentRefCount()
  }

  private clearDestroyedState(): void {
    this.activePlacements.clear()
    this.pendingDecrements.clear()
    this.pendingVisibleAdds.clear()
    this.filePlacements.length = 0

    for (const nodeInfo of this.nodeInfos) {
      nodeInfo.reset()
      nodeInfo.inst = null
    }
    this.nodeInfos.length = 0

    this._nodeIndices = null
    this.lodMinDistThresholds = null
    this.environmentPlacement = null
    this.forceRefresh = false
    this.needsLodUpdate = false
    this.dirtyModifiedPlacements = false
    this.loadedThisFrame = 0
    this.renderer = null
  }

  /**
   * 返回当前引用的文件索引数组（用于销毁时 decRef）。
   *
   * @returns 需减少引用计数的 fileIndex 列表
   */
  getFileDecrements(): number[] {
    const toRelease: number[] = []
    for (let i = 0; i < this.filePlacements.length; i++) {
      if (this.filePlacements[i]) {
        toRelease.push(i)
      }
    }
    return toRelease
  }

  /**
   * 更新 LOD（主流程）
   *
   * @param cameraNode - 相机节点
   * @param params - 全局参数
   */
  updateLod(cameraNode: THREE.Object3D, params: GSplatParams): void {
    if (this.octree.nodes.length === 0) {
      return // 提前返回，避免后续错误
    }

    const maxLod = this.octree.lodLevels - 1
    const lodBaseDistance = this.placement.lodBaseDistance
    const lodMultiplier = this.placement.lodMultiplier

    // 限制 LOD 范围到有效边界 [0, maxLod] 并确保 min <= max
    const { lodRangeMin, lodRangeMax } = params
    const rangeMin = Math.max(0, Math.min(lodRangeMin ?? 0, maxLod))
    const rangeMax = Math.max(rangeMin, Math.min(lodRangeMax ?? maxLod, maxLod))

    // Pass 1: 评估每个节点的最优 LOD（基于距离）
    const totalOptimalSplats = this.evaluateNodeLods(
      cameraNode,
      maxLod,
      lodBaseDistance,
      lodMultiplier,
      rangeMin,
      rangeMax,
      params
    )

    // 执行 Splat 预算（如果启用）
    if (this.splatBudget > 0) {
      this.enforceSplatBudget(totalOptimalSplats, this.splatBudget, rangeMin, rangeMax)
    }

    // Pass 2: 计算期望 LOD（underfill）并应用更改
    this.applyLodChanges(maxLod, params)
  }

  /**
   * Pass 1: 评估所有节点的最优 LOD 索引
   *
   * 基于相机位置和参数计算每个节点的最优 LOD
   * 结果存储在 nodeInfos 数组中
   *
   * @param cameraNode - 相机节点
   * @param maxLod - 最大 LOD 索引（lodLevels - 1）
   * @param lodDistances - LOD 距离阈值数组
   * @param rangeMin - 最小允许 LOD 索引
   * @param rangeMax - 最大允许 LOD 索引
   * @param params - 全局参数
   * @returns 最优 LOD 的总 Splat 数量
   */
  private evaluateNodeLods(
    cameraNode: THREE.Object3D,
    maxLod: number,
    lodBaseDistance: number,
    lodMultiplier: number,
    rangeMin: number,
    rangeMax: number,
    params: GSplatParams,
    accumulateSplats = true,
    globalMaxDistanceForBuckets = 0
  ): number {
    const { lodBehindPenalty } = params
    const safeBaseDistance = Math.max(0.0001, lodBaseDistance)
    const safeMultiplier = Math.max(1.0001, lodMultiplier)
    const fovScale = this.getFovScale(cameraNode)

    // 将相机位置和方向转换到八叉树本地空间
    cameraNode.getWorldPosition(_worldCameraPos)
    cameraNode.getWorldDirection(_worldCameraFwd)

    this.placement.node.updateMatrixWorld(true)
    _invWorldMat.copy(this.placement.node.matrixWorld).invert()

    _localCameraPos.copy(_worldCameraPos).applyMatrix4(_invWorldMat)
    _localCameraFwd.copy(_worldCameraFwd).transformDirection(_invWorldMat).normalize()

    const nodes = this.octree.nodes
    const nodeInfos = this.nodeInfos
    let totalSplats = 0
    const uniformScale = this.getUniformScale()
    const clipState = this.clipState
    this._clipLodDiagnostics = createClipLodDiagnostics()

    // 使用最大 LOD 范围的距离阈值来归一化重要性
    const minDistThresholds =
      maxLod >= 1 ? this.ensureLodMinDistThresholds(maxLod, safeBaseDistance, safeMultiplier) : null
    const maxDistance =
      rangeMax >= 1
        ? safeBaseDistance * safeMultiplier ** Math.max(0, rangeMax - 1)
        : safeBaseDistance

    for (let nodeIndex = 0; nodeIndex < nodes.length; nodeIndex++) {
      const node = nodes[nodeIndex]
      const nodeInfo = nodeInfos[nodeIndex]

      if (clipState?.volumes.length) {
        const classification = classifyGSplatClipNode(
          clipState,
          node.bounds,
          this.placement.node.matrixWorld
        )
        const diagnostics = this._clipLodDiagnostics
        diagnostics.classifiedNodes++
        if (classification.hidden) {
          nodeInfo.optimalLod = -1
          nodeInfo.importance = 0
          nodeInfo.worldDistance = 0
          nodeInfo.budgetBucket = 0
          const estimatedLod = node.lods[rangeMin]
          diagnostics.excludedSplats += estimatedLod?.count ?? 0
          if (classification.cullKind === 'polygon') diagnostics.polygonCulledNodes++
          if (classification.cullKind === 'box') diagnostics.boxCulledNodes++
          if (classification.cullKind === 'mixed') diagnostics.mixedCulledNodes++
          continue
        }
        diagnostics.intersectingNodes += classification.intersectsBoundary ? 1 : 0
      }

      // 计算到包围盒最近点的距离
      node.bounds.clampPoint(_localCameraPos, _closestPoint)
      _dirToNode.subVectors(_closestPoint, _localCameraPos)
      const actualDistance = _dirToNode.length()

      // 应用背后惩罚（如果启用）
      let penalizedDistance = actualDistance
      let importanceMultiplier = 1.0

      if (lodBehindPenalty > 1 && actualDistance > 0.01) {
        // 使用未归一化的方向计算点积，避免额外的归一化；除以距离
        const dotOverDistance = _localCameraFwd.dot(_dirToNode) / actualDistance

        // 只在相机后方应用惩罚（dot < 0）
        if (dotOverDistance < 0) {
          const t = -dotOverDistance // 0 .. 1 从正前方到正后方
          const factor = 1 + t * (lodBehindPenalty - 1)
          penalizedDistance = actualDistance * factor
          importanceMultiplier = 1.0 / factor // 重要性的逆向因子
        }
      }

      // 根据惩罚距离选择 LOD
      const fovAdjustedDistance = penalizedDistance * fovScale
      let optimalLodIndex: number
      if (maxLod === 0 || fovAdjustedDistance < safeBaseDistance) {
        optimalLodIndex = 0
      } else {
        optimalLodIndex = maxLod
        while (
          minDistThresholds &&
          optimalLodIndex > 1 &&
          fovAdjustedDistance < minDistThresholds[optimalLodIndex]
        ) {
          optimalLodIndex--
        }
      }

      // 限制到配置范围
      if (optimalLodIndex < rangeMin) optimalLodIndex = rangeMin
      if (optimalLodIndex > rangeMax) optimalLodIndex = rangeMax

      // 计算重要性：距离的倒数，归一化，带背后惩罚
      const normalizedDistance = Math.min(fovAdjustedDistance / maxDistance, 1.0)
      const importance = (1.0 - normalizedDistance) * importanceMultiplier

      // 存储最优 LOD 和重要性
      nodeInfo.optimalLod = optimalLodIndex
      nodeInfo.importance = importance
      nodeInfo.worldDistance = fovAdjustedDistance * uniformScale

      if (globalMaxDistanceForBuckets > 0 && optimalLodIndex >= 0) {
        const bucketScale = NUM_BUCKETS / Math.sqrt(globalMaxDistanceForBuckets)
        const bucket = Math.floor(Math.sqrt(nodeInfo.worldDistance) * bucketScale)
        nodeInfo.budgetBucket = Math.min(Math.max(bucket, 0), NUM_BUCKETS - 1)
      }

      // 统计最优 LOD 的 Splat 数量
      if (accumulateSplats) {
        const lod = nodes[nodeIndex].lods[optimalLodIndex]
        if (lod?.count) {
          totalSplats += lod.count
        }
      }
    }

    return totalSplats
  }

  evaluateOptimalLods(
    cameraNode: THREE.Object3D,
    params: GSplatParams,
    budgetScale = 1,
    globalMaxDistanceForBuckets = 0
  ): number {
    const maxLod = this.octree.lodLevels - 1
    const { lodRangeMin, lodRangeMax } = params
    const rangeMin = Math.max(0, Math.min(lodRangeMin ?? 0, maxLod))
    const rangeMax = Math.max(rangeMin, Math.min(lodRangeMax ?? maxLod, maxLod))
    this.rangeMin = rangeMin
    this.rangeMax = rangeMax

    const effectiveBase = this.placement.lodBaseDistance * budgetScale
    const effectiveMult = Math.max(1.2, this.placement.lodMultiplier * budgetScale ** -0.2)

    return this.evaluateNodeLods(
      cameraNode,
      maxLod,
      effectiveBase,
      effectiveMult,
      rangeMin,
      rangeMax,
      params,
      true,
      globalMaxDistanceForBuckets
    )
  }

  private ensureLodMinDistThresholds(
    maxLod: number,
    baseDistance: number,
    multiplier: number
  ): Float32Array {
    const requiredLength = maxLod + 1
    if (!this.lodMinDistThresholds || this.lodMinDistThresholds.length < requiredLength) {
      this.lodMinDistThresholds = new Float32Array(requiredLength)
    }

    let threshold = baseDistance
    this.lodMinDistThresholds[1] = threshold
    for (let index = 2; index <= maxLod; index++) {
      threshold *= multiplier
      this.lodMinDistThresholds[index] = threshold
    }

    return this.lodMinDistThresholds
  }

  private getFovScale(cameraNode: THREE.Object3D): number {
    const camera = cameraNode as THREE.PerspectiveCamera
    if (!camera.isPerspectiveCamera) {
      return 1
    }

    const tanHalfVFov = Math.tan(THREE.MathUtils.degToRad(camera.fov * 0.5))
    const tanHalfHFov = tanHalfVFov * camera.aspect
    return Math.min(tanHalfVFov, tanHalfHFov) / REF_TAN_HALF_FOV
  }

  private getUniformScale(): number {
    this.placement.node.updateMatrixWorld(true)
    _worldCameraPos.setFromMatrixScale(this.placement.node.matrixWorld)
    return Math.max(_worldCameraPos.x, _worldCameraPos.y, _worldCameraPos.z, 0.0001)
  }

  /**
   * 执行 Splat 预算约束（双向调整）
   *
   * 超预算时：从低重要性到高重要性降级
   * 低预算时：从高重要性到低重要性升级
   *
   * @param totalSplats - 当前总 Splat 数量
   * @param splatBudget - 目标 Splat 预算
   * @param rangeMin - 最小允许 LOD 索引
   * @param rangeMax - 最大允许 LOD 索引
   */
  private enforceSplatBudget(
    totalSplats: number,
    splatBudget: number,
    rangeMin: number,
    rangeMax: number
  ): void {
    const nodes = this.octree.nodes
    const nodeInfos = this.nodeInfos

    // 懒加载节点索引数组
    if (!this._nodeIndices) {
      this._nodeIndices = new Uint32Array(nodes.length)
      for (let i = 0; i < nodes.length; i++) {
        this._nodeIndices[i] = i
      }
    }

    // 按重要性排序（从低到高）
    const nodeIndices = this._nodeIndices
    nodeIndices.sort((a, b) => nodeInfos[a].importance - nodeInfos[b].importance)

    let currentSplats = totalSplats

    // 已在预算范围内
    if (currentSplats === splatBudget) {
      return
    }

    // 确定方向和迭代参数
    const isOverBudget = currentSplats > splatBudget
    const lodDelta = isOverBudget ? 1 : -1

    // 多次迭代：每次调整一级 LOD，直到达到预算
    while (isOverBudget ? currentSplats > splatBudget : currentSplats < splatBudget) {
      let modified = false

      if (isOverBudget) {
        // 降级：从低重要性到高重要性处理
        for (let i = 0; i < nodeIndices.length; i++) {
          const nodeIndex = nodeIndices[i]
          const nodeInfo = nodeInfos[nodeIndex]
          const node = nodes[nodeIndex]
          const currentOptimalLod = nodeInfo.optimalLod

          // 尝试降级到下一级粗糙 LOD（尊重 rangeMax 约束）
          if (currentOptimalLod >= 0 && currentOptimalLod < rangeMax) {
            const currentLod = node.lods[currentOptimalLod]
            const nextLod = node.lods[currentOptimalLod + 1]
            const splatsSaved = currentLod.count - nextLod.count

            // 降级到粗糙 LOD
            nodeInfo.optimalLod += lodDelta
            currentSplats -= splatsSaved
            modified = true

            if (currentSplats <= splatBudget) {
              break // 达到预算
            }
          }
        }
      } else {
        // 升级：从高重要性到低重要性处理
        for (let i = nodeIndices.length - 1; i >= 0; i--) {
          const nodeIndex = nodeIndices[i]
          const nodeInfo = nodeInfos[nodeIndex]
          const node = nodes[nodeIndex]
          const currentOptimalLod = nodeInfo.optimalLod

          // 尝试升级到下一级精细 LOD（尊重 rangeMin 约束）
          if (currentOptimalLod >= 0 && currentOptimalLod > rangeMin) {
            const currentLod = node.lods[currentOptimalLod]
            const nextLod = node.lods[currentOptimalLod - 1]
            const splatsAdded = nextLod.count - currentLod.count

            // 只在不超预算时升级
            if (currentSplats + splatsAdded <= splatBudget) {
              // 升级到精细 LOD
              nodeInfo.optimalLod += lodDelta
              currentSplats += splatsAdded
              modified = true

              if (currentSplats >= splatBudget) {
                break // 达到预算
              }
            }
          }
        }
      }

      // 如果没有节点被修改，无法进一步调整
      if (!modified) {
        break
      }
    }
  }

  /**
   * Pass 2: 应用计算的 LOD 更改并管理文件 Placement
   *
   * 读取 evaluateNodeLods() 填充的 nodeInfos 数组
   *
   * @param maxLod - 最大 LOD 索引
   * @param params - 全局参数
   */
  applyLodChanges(maxLod: number, params: GSplatParams): void {
    const nodes = this.octree.nodes
    const { lodUnderfillLimit = 0, lodRefinementMode = 'step', maxLoadsPerFrame = 0 } = params

    // 重置当前帧加载计数器
    this.loadedThisFrame = 0

    // 首先处理待处理的可见添加：检查资源是否已加载完成
    if (this.pendingVisibleAdds.size > 0) {
      const toPromote: number[] = []
      for (const [nodeIndex, fileIndex] of this.pendingVisibleAdds) {
        const placement = this.filePlacements[fileIndex]
        if (placement?.resource) {
          // 资源已准备好，可以将节点标记为可见
          toPromote.push(nodeIndex)
        }
      }

      // 将节点标记为可见
      for (const nodeIndex of toPromote) {
        const fileIndex = this.pendingVisibleAdds.get(nodeIndex)!
        const node = nodes[nodeIndex]

        // 找到对应的 LOD 索引
        let lodIndex = 0
        for (let li = 0; li < node.lods.length; li++) {
          if (node.lods[li].fileIndex === fileIndex) {
            lodIndex = li
            break
          }
        }

        this.nodeInfos[nodeIndex].currentLod = lodIndex
        this.pendingVisibleAdds.delete(nodeIndex)
      }
    }

    for (let nodeIndex = 0; nodeIndex < nodes.length; nodeIndex++) {
      const node = nodes[nodeIndex]
      const nodeInfo = this.nodeInfos[nodeIndex]

      const optimalLodIndex = nodeInfo.optimalLod
      const currentLodIndex = nodeInfo.currentLod

      // 应用 underfill 策略确定期望 LOD
      const desiredLodIndex = this.selectDesiredLodIndex(
        node,
        optimalLodIndex,
        maxLod,
        lodUnderfillLimit
      )

      if (desiredLodIndex < 0) {
        this.cancelNodeTransitions(nodeIndex, currentLodIndex)
        continue
      }

      // 如果期望 LOD 与当前显示的 LOD 不同
      if (desiredLodIndex !== currentLodIndex) {
        // 确定可见性
        const currentFileIndex = currentLodIndex >= 0 ? node.lods[currentLodIndex].fileIndex : -1
        const desiredFileIndex = desiredLodIndex >= 0 ? node.lods[desiredLodIndex].fileIndex : -1
        const wasVisible = currentFileIndex !== -1
        const willBeVisible = desiredFileIndex !== -1

        // 如果有待处理的转换，管理它而不丢弃当前可见的 LOD
        const pendingEntry = this.pendingDecrements.get(nodeIndex)
        if (pendingEntry) {
          // 如果期望目标在前一个目标仍在加载时改变，取消前一个目标
          if (pendingEntry.newFileIndex !== desiredFileIndex) {
            // 从之前待处理的目标移除此节点的区间
            const prevPendingPlacement = this.filePlacements[pendingEntry.newFileIndex]
            if (prevPendingPlacement) {
              this.decrementFileRef(pendingEntry.newFileIndex, nodeIndex)
            }

            // 更新或清除待处理的转换
            if (wasVisible && willBeVisible) {
              this.pendingDecrements.set(nodeIndex, {
                oldFileIndex: pendingEntry.oldFileIndex,
                newFileIndex: desiredFileIndex,
              })
            } else {
              // 不再针对可见 LOD；清除待处理并让正常逻辑处理隐藏/显示
              this.pendingDecrements.delete(nodeIndex)
            }
          }
          // 如果目标保持不变，保持待处理直到资源加载
        }

        if (!wasVisible && willBeVisible) {
          // 变为可见（不可见 -> 可见）

          // 如果之前有不同文件的待处理可见添加，取消它
          const prevPendingFi = this.pendingVisibleAdds.get(nodeIndex)
          if (prevPendingFi !== undefined && prevPendingFi !== desiredFileIndex) {
            this.decrementFileRef(prevPendingFi, nodeIndex)
            this.pendingVisibleAdds.delete(nodeIndex)
          }

          // ⭐ 检查加载限制（仅对新文件加载）
          const willTriggerLoad = !this.filePlacements[desiredFileIndex]
          const canLoad = maxLoadsPerFrame === 0 || this.loadedThisFrame < maxLoadsPerFrame

          if (!willTriggerLoad || canLoad) {
            // 未超过限制，允许加载
            if (willTriggerLoad) {
              this.loadedThisFrame++
            }

            this.incrementFileRef(desiredFileIndex, nodeIndex, desiredLodIndex)
            const newPlacement = this.filePlacements[desiredFileIndex]
            if (newPlacement?.resource) {
              // 资源现在准备好了，立即显示
              nodeInfo.currentLod = desiredLodIndex
              // 清除任何待处理的可见添加条目
              this.pendingVisibleAdds.delete(nodeIndex)
            } else {
              // 保持显示为不可见直到资源到达；下次更新将晋升
              this.pendingVisibleAdds.set(nodeIndex, desiredFileIndex)
            }
          }
          // 如果超过限制，则跳过此节点，等待下一帧
        } else if (wasVisible && !willBeVisible) {
          // 变为不可见（可见 -> 不可见）

          // 如果此节点有待处理的目标，先取消它
          const pendingEntry2 = this.pendingDecrements.get(nodeIndex)
          if (pendingEntry2) {
            this.decrementFileRef(pendingEntry2.newFileIndex, nodeIndex)
            this.pendingDecrements.delete(nodeIndex)
          }
          this.decrementFileRef(currentFileIndex, nodeIndex)
          nodeInfo.currentLod = -1
          // 清除任何待处理的可见添加条目
          this.pendingVisibleAdds.delete(nodeIndex)
        } else if (wasVisible && willBeVisible) {
          // 在可见 LOD 之间切换（可见 -> 可见）

          // ⭐ 检查加载限制（仅对新文件加载）
          const willTriggerLoad = !this.filePlacements[desiredFileIndex]
          const canLoad = maxLoadsPerFrame === 0 || this.loadedThisFrame < maxLoadsPerFrame

          if (!willTriggerLoad || canLoad) {
            // 未超过限制，允许加载
            if (willTriggerLoad) {
              this.loadedThisFrame++
            }

            this.incrementFileRef(desiredFileIndex, nodeIndex, desiredLodIndex)

            const newPlacement = this.filePlacements[desiredFileIndex]
            if (newPlacement?.resource) {
              // 新 LOD 准备好后，旧 LOD 继续使用默认冷却，
              // 避免相机往返时重复下载和解码。
              this.decrementFileRef(currentFileIndex, nodeIndex)
              // 清除此节点的任何待处理
              this.pendingDecrements.delete(nodeIndex)
              // 更新显示的 LOD
              nodeInfo.currentLod = desiredLodIndex
              // 清除任何待处理的可见添加条目
              this.pendingVisibleAdds.delete(nodeIndex)
            } else {
              // 新 LOD 未准备好 - 跟踪待处理的减少
              this.pendingDecrements.set(nodeIndex, {
                oldFileIndex: currentFileIndex,
                newFileIndex: desiredFileIndex,
              })
              // 保持显示的 LOD 为当前，直到待处理解决
              // 确保没有待处理的可见添加条目
              this.pendingVisibleAdds.delete(nodeIndex)
            }
          }
          // 如果超过限制，则保持当前 LOD，等待下一帧
        }
      }

      // 预加载：粗 LOD 已经真正可见后，再请求朝向最优的下一个更好的 LOD。
      // 首次加载由 incrementFileRef 负责 desired LOD，不应在它仍在加载时
      // 同时启动下一精细 LOD。
      if (
        desiredLodIndex > optimalLodIndex &&
        this.isDesiredLodReadyAndVisible(node, nodeIndex, desiredLodIndex)
      ) {
        this.prefetchNextLod(node, desiredLodIndex, optimalLodIndex, lodRefinementMode)
      }
    }
  }

  /**
   * 检查 desired LOD 是否已经是节点当前显示的 LOD，且已接入活跃渲染集合。
   */
  private isDesiredLodReadyAndVisible(
    node: GSplatOctreeNode,
    nodeIndex: number,
    desiredLodIndex: number
  ): boolean {
    if (desiredLodIndex < 0 || this.nodeInfos[nodeIndex].currentLod !== desiredLodIndex) {
      return false
    }

    const fileIndex = node.lods[desiredLodIndex]?.fileIndex ?? -1
    if (fileIndex === -1) {
      return false
    }

    const placement = this.filePlacements[fileIndex]
    if (!placement?.resource) {
      return false
    }

    return placement.intervals.has(nodeIndex) && this.activePlacements.has(placement)
  }

  /**
   * 使用 underfill 策略选择期望的 LOD 索引
   *
   * 当 underfill 启用时，优先选择 [optimalLodIndex .. optimalLodIndex + lodUnderfillLimit]
   * 范围内已加载的 LOD。如果都未加载，选择范围内最粗糙的可用 LOD。
   *
   * @param node - 八叉树节点
   * @param optimalLodIndex - 最优 LOD 索引
   * @param maxLod - 最大 LOD 索引
   * @param lodUnderfillLimit - 允许的粗糙范围
   * @returns 期望的 LOD 索引
   */
  private selectDesiredLodIndex(
    node: GSplatOctreeNode,
    optimalLodIndex: number,
    maxLod: number,
    lodUnderfillLimit: number
  ): number {
    if (optimalLodIndex < 0) return -1

    if (lodUnderfillLimit > 0) {
      const allowedMaxCoarseLod = Math.min(maxLod, optimalLodIndex + lodUnderfillLimit)

      // 优先选择允许范围内已加载的最高质量
      for (let lod = optimalLodIndex; lod <= allowedMaxCoarseLod; lod++) {
        const fi = node.lods[lod].fileIndex
        if (fi !== -1 && this.octree.getFileResource(fi)) {
          return lod // 返回已加载的
        }
      }

      // 回退：选择范围内最粗糙的可用
      for (let lod = allowedMaxCoarseLod; lod >= optimalLodIndex; lod--) {
        const fi = node.lods[lod].fileIndex
        if (fi !== -1) return lod
      }
    }

    return optimalLodIndex
  }

  /** 叶节点被粗裁后，立即撤销其加载转换并移除当前 Placement 区间。 */
  private cancelNodeTransitions(nodeIndex: number, currentLodIndex: number): void {
    const pendingVisibleFileIndex = this.pendingVisibleAdds.get(nodeIndex)
    if (pendingVisibleFileIndex !== undefined) {
      this.decrementFileRef(pendingVisibleFileIndex, nodeIndex)
      this.pendingVisibleAdds.delete(nodeIndex)
    }

    const pendingEntry = this.pendingDecrements.get(nodeIndex)
    if (pendingEntry) {
      this.decrementFileRef(pendingEntry.newFileIndex, nodeIndex)
      this.pendingDecrements.delete(nodeIndex)
    }

    if (currentLodIndex >= 0) {
      const currentFileIndex = this.octree.nodes[nodeIndex].lods[currentLodIndex]?.fileIndex ?? -1
      this.decrementFileRef(currentFileIndex, nodeIndex)
      this.nodeInfos[nodeIndex].currentLod = -1
    }
  }

  /**
   * 预加载朝向最优的后续 LOD
   *
   * 这会分阶段在所有节点上进行加载，避免在粗糙数据存在之前混合请求
   *
   * @param node - 八叉树节点
   * @param desiredLodIndex - 当前选择显示的 LOD（可能比最优粗糙）
   * @param optimalLodIndex - 目标最优 LOD
   * @param refinementMode - 逐级细化或直接准备当前最优档
   */
  private prefetchNextLod(
    node: GSplatOctreeNode,
    desiredLodIndex: number,
    optimalLodIndex: number,
    refinementMode: GSplatLodRefinementMode
  ): void {
    if (desiredLodIndex === -1 || optimalLodIndex === -1) return

    // 如果我们已经在最优但尚未加载，请求它
    if (desiredLodIndex === optimalLodIndex) {
      const fi = node.lods[optimalLodIndex].fileIndex
      if (fi !== -1) {
        this.octree.ensureFileResource(fi)
        if (!this.octree.getFileResource(fi)) {
          this.addFileLoadInterest(this.prefetchPending, fi)
        }
      }
      return
    }

    const direct = refinementMode === 'direct'
    const startLod = direct ? optimalLodIndex : Math.max(optimalLodIndex, desiredLodIndex - 1)
    const endLod = direct ? desiredLodIndex : optimalLodIndex
    const step = direct ? 1 : -1

    // step 从相邻细档开始，direct 从最优档开始；遇到空档时都退到最近的有效文件。
    for (let lod = startLod; direct ? lod < endLod : lod >= endLod; lod += step) {
      const fi = node.lods[lod].fileIndex
      if (fi !== -1) {
        this.octree.ensureFileResource(fi)
        if (!this.octree.getFileResource(fi)) {
          this.addFileLoadInterest(this.prefetchPending, fi)
        }
        break
      }
    }
  }

  /**
   * 增加文件引用计数并立即创建 Placement
   *
   * @param fileIndex - 文件索引
   * @param nodeIndex - 八叉树节点索引
   * @param lodIndex - 此节点的 LOD 索引
   */
  private incrementFileRef(fileIndex: number, nodeIndex: number, lodIndex: number): void {
    if (fileIndex === -1) return

    // 检查是否为第一次引用
    let placement = this.filePlacements[fileIndex]
    if (!placement) {
      // 创建 Placement（初始资源为 null）
      placement = new GSplatPlacement(null, this.placement.node, lodIndex)
      placement.workBufferModifier = this.placement.workBufferModifier
      placement.workBufferUpdateMode = this.placement.workBufferUpdateMode
      this.filePlacements[fileIndex] = placement

      // 增加 Octree 的文件引用计数
      this.octree.incRefCount(fileIndex)

      // 如果资源已加载，允许使用
      const resourceExists = this.addFilePlacement(fileIndex)
      if (!resourceExists) {
        // 资源尚未加载，启动加载并添加到待处理
        this.octree.ensureFileResource(fileIndex)
        this.addFileLoadInterest(this.pending, fileIndex)
      }
    }

    // 为此节点添加区间到 Placement
    const nodes = this.octree.nodes
    const node = nodes[nodeIndex]
    const lod = node.lods[lodIndex]

    // 创建区间 [start, end]（闭区间）
    const interval: Interval = {
      start: lod.offset,
      end: lod.offset + lod.count - 1,
    }
    placement.intervals.set(nodeIndex, interval)

    this.dirtyModifiedPlacements = true
  }

  /**
   * 减少文件引用计数并在需要时移除 Placement
   *
   * @param fileIndex - 文件索引
   * @param nodeIndex - 八叉树节点索引
   */
  private decrementFileRef(fileIndex: number, nodeIndex: number, cooldownTicks?: number): void {
    if (fileIndex === -1) return

    const placement = this.filePlacements[fileIndex]
    if (!placement) {
      return
    }

    // 从 Placement 移除此节点的区间
    placement.intervals.delete(nodeIndex)
    this.dirtyModifiedPlacements = true

    // 如果这是最后一个引用，移除 Placement 并立即减少引用计数
    if (placement.intervals.size === 0) {
      // 只在已添加（有资源）时移除
      if (placement.resource) {
        this.activePlacements.delete(placement)
      }

      // 清空 Placement 引用，避免保留大量无用对象
      this.filePlacements[fileIndex] = null

      // 立即减少 Octree 的文件引用计数
      if (cooldownTicks === undefined) {
        this.octree.decRefCount(fileIndex)
      } else {
        this.octree.decRefCount(fileIndex, cooldownTicks)
      }

      // 从待处理集合中移除
      this.cancelFileLoadInterest(this.pending, fileIndex)
    }
  }

  /**
   * 用加载的资源更新现有 Placement 并添加到管理器
   *
   * @param fileIndex - 文件索引
   * @returns 如果 Placement 已更新并添加到管理器则为 true
   */
  private addFilePlacement(fileIndex: number): boolean {
    const res = this.octree.getFileResource(fileIndex)
    if (res) {
      // 获取现有 Placement 并更新其资源
      const placement = this.filePlacements[fileIndex]
      if (placement) {
        placement.resource = res

        // 类型安全的 AABB 访问
        const resource = res as IGSplatResource
        if (resource.aabb) {
          placement.aabb.copy(resource.aabb)
        } else {
          placement.aabb.copy(new THREE.Box3())
        }

        this.activePlacements.add(placement)
        this.dirtyModifiedPlacements = true
        if (!this.firstActivePlacementLogged) {
          this.firstActivePlacementLogged = true
          log.debug(
            `[GSplatOctreeInstance] first active placement, fileIndex=${fileIndex}, activePlacements=${this.activePlacements.size}, pending=${this.pending.size}`
          )
        }
        return true
      }
    }
    return false
  }

  /**
   * 测试八叉树实例是否移动超过提供的 LOD 更新距离
   *
   * @param threshold - 触发更新的距离阈值
   * @returns 如果移动超过阈值则为 true
   */
  testMoved(threshold: number): boolean {
    this.placement.node.getWorldPosition(_worldCameraPos)
    const length = _worldCameraPos.distanceTo(this.previousPosition)
    return length > threshold
  }

  /**
   * 更新八叉树实例的上次位置
   */
  updateMoved(): void {
    this.placement.node.getWorldPosition(this.previousPosition)
  }

  /**
   * 每帧更新八叉树实例
   *
   * @returns 如果八叉树实例脏则为 true
   */
  update(): boolean {
    // 如果实例不可见，检查是否有 dirty 标志需要返回
    if (!this._visible) {
      // 即使不可见，也要返回 dirty 标志，让 Manager 知道需要更新渲染列表
      const wasDirty = this.dirtyModifiedPlacements
      this.dirtyModifiedPlacements = false
      return wasDirty
    }

    // 从 placement 同步 splat 预算并检测更改
    const currentBudget = this.placement.splatBudget
    if (currentBudget !== this.splatBudget) {
      this.splatBudget = currentBudget
      this.needsLodUpdate = true
    }

    // 预算为0时发出警告（仅一次）
    if (this.splatBudget === 0 && !this._warnedBudget) {
      log.warn('[GSplatOctreeInstance] splatBudget is 0')
      this._warnedBudget = true
    }

    // 处理上下文恢复后的强制刷新
    if (this.forceRefresh) {
      this.forceRefresh = false
      // 标记所有 Placement 需要重新加载
      for (let fileIndex = 0; fileIndex < this.filePlacements.length; fileIndex++) {
        const placement = this.filePlacements[fileIndex]
        if (placement) {
          // 重新请求资源
          this.octree.ensureFileResource(fileIndex)
          if (!this.octree.getFileResource(fileIndex)) {
            this.addFileLoadInterest(this.pending, fileIndex)
          }
        }
      }
    }

    // 处理待处理的加载
    if (this.pending.size) {
      for (const fileIndex of this.pending) {
        // 检查资源是否完成加载并存储
        this.octree.ensureFileResource(fileIndex)

        // 如果资源可用，更新 Placement 并执行任何待处理的减少
        if (this.addFilePlacement(fileIndex)) {
          _tempCompletedUrls.push(fileIndex)

          // 执行 newFileIndex 匹配的节点的任何待处理减少
          for (const [nodeIndex, { oldFileIndex, newFileIndex }] of this.pendingDecrements) {
            if (newFileIndex === fileIndex) {
              // 将显示的 LOD 设置为映射到新准备好的文件的 LOD
              let newLodIndex = 0
              const nodeLods = this.octree.nodes[nodeIndex].lods
              for (let li = 0; li < nodeLods.length; li++) {
                if (nodeLods[li].fileIndex === newFileIndex) {
                  newLodIndex = li
                  break
                }
              }
              this.decrementFileRef(oldFileIndex, nodeIndex)
              this.pendingDecrements.delete(nodeIndex)
              this.nodeInfos[nodeIndex].currentLod = newLodIndex
            }
          }
        }
      }

      // 如果有资源完成，标记 LOD 更新
      if (_tempCompletedUrls.length > 0) {
        this.needsLodUpdate = true
      }

      // 从待处理中移除已完成的项
      for (const fileIndex of _tempCompletedUrls) {
        this.completeFileLoadInterest(this.pending, fileIndex)
      }

      // 清空临时数组
      _tempCompletedUrls.length = 0
    }

    // 观察预加载完成以允许晋升
    this.pollPrefetchCompletions()

    // 处理 environment 加载
    if (this.octree.environmentUrl && !this.environmentPlacement) {
      // 轮询 environment 资源完成
      this.octree.ensureEnvironmentResource()
      const envResource = this.octree.environmentResource

      if (envResource) {
        // 用加载的资源创建 environment Placement
        this.environmentPlacement = new GSplatPlacement(envResource, this.placement.node, 0)
        this.environmentPlacement.isEnvironment = true
        this.environmentPlacement.workBufferModifier = this.placement.workBufferModifier
        this.environmentPlacement.workBufferUpdateMode = this.placement.workBufferUpdateMode

        // 类型安全的 AABB 访问
        const resource = envResource as IGSplatResource
        if (resource.aabb) {
          this.environmentPlacement.aabb.copy(resource.aabb)
        } else {
          this.environmentPlacement.aabb.copy(new THREE.Box3())
        }

        this.dirtyModifiedPlacements = true
        this.syncEnvironmentVisibility()
      }
    }

    // Environment 的活动状态同时受整资产和独立可见性控制。
    this.syncEnvironmentVisibility()

    // 检查是否有 Placement 需要 LOD 更新
    const dirty = this.dirtyModifiedPlacements
    this.dirtyModifiedPlacements = false
    return dirty
  }

  /**
   * 返回此实例是否请求 LOD 重新评估，并在返回后重置标志。
   *
   * @returns 本次调用前的标志值
   */
  consumeNeedsLodUpdate(): boolean {
    const v = this.needsLodUpdate
    this.needsLodUpdate = false
    return v
  }

  /**
   * 当前实例是否仍有尚未提交到最终渲染帧的 LOD 变化。
   *
   * 资源下载完成后，节点 LOD、Placement、WorldState 和排序结果会分阶段更新。
   * 该状态用于区分“LOD 已选中”和“LOD 已真正呈现在画布上”。
   */
  get lodPresentationPending(): boolean {
    if (!this._visible) {
      return false
    }

    if (this.forceRefresh || this.needsLodUpdate || this.dirtyModifiedPlacements) {
      return true
    }

    for (const fileIndex of this.pending) {
      if (!this.octree.isFileFailed(fileIndex)) return true
    }
    for (const fileIndex of this.prefetchPending) {
      if (!this.octree.isFileFailed(fileIndex)) return true
    }
    for (const { newFileIndex } of this.pendingDecrements.values()) {
      if (!this.octree.isFileFailed(newFileIndex)) return true
    }
    for (const fileIndex of this.pendingVisibleAdds.values()) {
      if (!this.octree.isFileFailed(fileIndex)) return true
    }

    return false
  }

  /**
   * 当前实例是否已有可在下一帧继续推进的 LOD 提交工作。
   *
   * 与 `lodPresentationPending` 不同，这里排除仍在等待网络或解析的资源，
   * 避免按需渲染在异步加载期间空转；资源就绪事件会再唤醒渲染。
   */
  get lodPresentationWorkPending(): boolean {
    if (!this._visible) {
      return false
    }

    if (this.forceRefresh || this.needsLodUpdate || this.dirtyModifiedPlacements) {
      return true
    }

    for (const fileIndex of this.pending) {
      if (!this.octree.isFileFailed(fileIndex) && this.octree.getFileResource(fileIndex))
        return true
    }
    for (const fileIndex of this.prefetchPending) {
      if (!this.octree.isFileFailed(fileIndex) && this.octree.getFileResource(fileIndex))
        return true
    }
    for (const { newFileIndex } of this.pendingDecrements.values()) {
      if (!this.octree.isFileFailed(newFileIndex) && this.octree.getFileResource(newFileIndex)) {
        return true
      }
    }
    for (const fileIndex of this.pendingVisibleAdds.values()) {
      if (!this.octree.isFileFailed(fileIndex) && this.octree.getFileResource(fileIndex))
        return true
    }

    return false
  }

  setWorkBufferModifier(modifier: CompiledGSplatWorkBufferModifier | null): void {
    this.placement.workBufferModifier = modifier

    for (const placement of this.filePlacements) {
      if (placement) {
        placement.workBufferModifier = modifier
      }
    }

    if (this.environmentPlacement) {
      this.environmentPlacement.workBufferModifier = modifier
    }
  }

  /** 更新叶节点粗裁快照；连续版本只保留最新状态。 */
  setClipState(state: GSplatClipState | null): boolean {
    if (
      this.clipState === state ||
      (this.clipState && state && areGSplatClipStatesEqual(this.clipState, state))
    ) {
      this.placement.clipState = state
      return false
    }

    this.clipState = state
    this.placement.clipState = state
    this.needsLodUpdate = true
    return true
  }

  get clipLodDiagnostics(): GSplatClipLodDiagnostics {
    return { ...this._clipLodDiagnostics }
  }

  setWorkBufferUpdateMode(mode: GSplatWorkBufferUpdateMode): void {
    this.placement.workBufferUpdateMode = mode

    for (const placement of this.filePlacements) {
      if (placement) {
        placement.workBufferUpdateMode = mode
      }
    }

    if (this.environmentPlacement) {
      this.environmentPlacement.workBufferUpdateMode = mode
    }
  }

  consumeWorkBufferUpdateOnce(renderedPlacements?: ReadonlySet<GSplatPlacement>): void {
    if (
      (!renderedPlacements || renderedPlacements.has(this.placement)) &&
      this.placement.workBufferUpdateMode === GSPLAT_WORKBUFFER_UPDATE_ONCE
    ) {
      this.placement.workBufferUpdateMode = GSPLAT_WORKBUFFER_UPDATE_AUTO
    }

    for (const placement of this.filePlacements) {
      if (
        placement &&
        (!renderedPlacements || renderedPlacements.has(placement)) &&
        placement.workBufferUpdateMode === GSPLAT_WORKBUFFER_UPDATE_ONCE
      ) {
        placement.workBufferUpdateMode = GSPLAT_WORKBUFFER_UPDATE_AUTO
      }
    }

    if (
      this.environmentPlacement &&
      (!renderedPlacements || renderedPlacements.has(this.environmentPlacement)) &&
      this.environmentPlacement.workBufferUpdateMode === GSPLAT_WORKBUFFER_UPDATE_ONCE
    ) {
      this.environmentPlacement.workBufferUpdateMode = GSPLAT_WORKBUFFER_UPDATE_AUTO
    }
  }

  /**
   * 轮询预加载的文件索引完成并更新状态
   */
  private pollPrefetchCompletions(): void {
    if (this.prefetchPending.size) {
      // 轮询加载器并在准备好时存储资源
      for (const fileIndex of this.prefetchPending) {
        this.octree.ensureFileResource(fileIndex)
        if (this.octree.getFileResource(fileIndex)) {
          _tempCompletedUrls.push(fileIndex)
        }
      }

      // 从 prefetchPending 移除已完成的
      if (_tempCompletedUrls.length > 0) {
        this.needsLodUpdate = true
      }

      for (const fileIndex of _tempCompletedUrls) {
        this.completeFileLoadInterest(this.prefetchPending, fileIndex)
      }
      _tempCompletedUrls.length = 0
    }
  }

  /**
   * 返回待处理加载或预加载的资源数量（包括 environment）
   */
  get pendingLoadCount(): number {
    let count = this.activePendingLoadCount
    for (const fileIndex of this.prefetchPending) {
      if (!this.octree.isFileFailed(fileIndex)) {
        count++
      }
    }

    // 如果配置了 environment 但尚未加载，添加它
    if (
      this.octree.environmentUrl &&
      !this.environmentPlacement &&
      !this.octree.isEnvironmentFailed()
    ) {
      count++
    }

    return count
  }

  /** 返回尚未进入失败终态的主动加载数量。 */
  get activePendingLoadCount(): number {
    let count = 0
    for (const fileIndex of this.pending) {
      if (!this.octree.isFileFailed(fileIndex)) {
        count++
      }
    }
    return count
  }

  // ==================== 调试信息收集 ====================

  /**
   * 收集调试状态信息
   *
   * @returns 调试信息字符串
   *
   * @remarks
   * 用于诊断 LOD 评估和资源加载问题
   */
  collectDebugState(): string {
    const parts: string[] = []

    // 节点级别统计
    let visibleNodes = 0
    let pendingLodChange = 0
    const totalNodes = this.nodeInfos.length

    for (const info of this.nodeInfos) {
      if (info.currentLod >= 0) {
        visibleNodes++
      }
      if (info.optimalLod >= 0 && info.currentLod !== info.optimalLod) {
        pendingLodChange++
      }
    }

    parts.push(`nodes=${visibleNodes}/${totalNodes}`)
    parts.push(`pendingLodChange=${pendingLodChange}`)
    parts.push(`active=${this.activePlacements.size}`)
    parts.push(`pending=${this.pending.size}`)
    parts.push(`prefetch=${this.prefetchPending.size}`)
    parts.push(`pendingDecrements=${this.pendingDecrements.size}`)
    parts.push(`pendingVisibleAdds=${this.pendingVisibleAdds.size}`)
    parts.push(`needsLodUpdate=${this.needsLodUpdate}`)
    parts.push(`dirty=${this.dirtyModifiedPlacements}`)

    return parts.join(', ')
  }

  /**
   * 获取实例可见性状态
   */
  get visible(): boolean {
    return this._visible
  }

  /**
   * 将当前实例的 Environment Placement 与共享期望状态同步。
   *
   * 该操作只改变活动渲染集合，不卸载资源或修改引用计数。
   *
   * @returns 活动渲染集合发生变化时返回 true
   */
  syncEnvironmentVisibility(): boolean {
    const environmentPlacement = this.environmentPlacement
    if (!environmentPlacement?.resource) {
      return false
    }

    const shouldBeActive = this._visible && this.placement.environmentVisible
    const isActive = this.activePlacements.has(environmentPlacement)
    if (shouldBeActive === isActive) {
      return false
    }

    if (shouldBeActive) {
      this.activePlacements.add(environmentPlacement)
    } else {
      this.activePlacements.delete(environmentPlacement)
    }
    this.dirtyModifiedPlacements = true
    return true
  }

  /**
   * 设置实例可见性
   * 隐藏时仅清空 activePlacements（从渲染列表移除），不减少引用计数
   * 资源保留在内存中，由 LRU 机制统一管理
   * 显示时恢复 activePlacements（重新添加到渲染列表）
   *
   * @param value - 是否可见
   */
  set visible(value: boolean) {
    if (this._visible === value) {
      return // 状态未变化
    }

    this._visible = value

    if (!value) {
      // 隐藏：仅清空 activePlacements，资源保留在内存中
      // 不减少引用计数，避免立即销毁资源
      // 资源最终由 LRU 机制在内存压力下统一管理
      this.activePlacements.clear()

      // 不清空 filePlacements 和 environmentPlacement，保留资源引用
      // 不重置节点状态，保留 LOD 评估结果

      this.dirtyModifiedPlacements = true
    } else {
      // 显示：将已加载资源的 Placement 重新添加到 activePlacements
      for (let i = 0; i < this.filePlacements.length; i++) {
        const placement = this.filePlacements[i]
        if (placement?.resource) {
          this.activePlacements.add(placement)
        }
      }

      // Environment 仅在其独立期望状态为可见时恢复。
      this.syncEnvironmentVisibility()

      // 标记需要更新，确保立即生效
      this.dirtyModifiedPlacements = true
      this.needsLodUpdate = true // 触发 LOD 重新评估
    }
  }

  /**
   * 收集详细的节点 LOD 状态
   *
   * @param maxNodes 最多返回多少个节点的详细信息（默认 10）
   * @returns 节点状态数组
   */
  collectNodeLodDetails(maxNodes: number = 10): Array<{
    nodeIndex: number
    currentLod: number
    optimalLod: number
    importance: number
  }> {
    const result: Array<{
      nodeIndex: number
      currentLod: number
      optimalLod: number
      importance: number
    }> = []

    // 按重要性排序，优先显示重要的节点
    const sortedIndices = this.nodeInfos
      .map((info, index) => ({ info, index }))
      .sort((a, b) => b.info.importance - a.info.importance)
      .slice(0, maxNodes)

    for (const { info, index } of sortedIndices) {
      result.push({
        nodeIndex: index,
        currentLod: info.currentLod,
        optimalLod: info.optimalLod,
        importance: info.importance,
      })
    }

    return result
  }
}
